import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { signup, login } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Volume2, VolumeX } from 'lucide-react';

// Sound effects using Web Audio API
const useVaultSounds = () => {
  const audioContextRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playClick = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800 + Math.random() * 400;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.05);
    } catch (e) {}
  }, [soundEnabled, getAudioContext]);

  const playTurn = useCallback((direction) => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      filter.type = 'lowpass';
      filter.frequency.value = 2000;
      osc.type = 'sawtooth';
      osc.frequency.value = direction > 0 ? 150 : 120;
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {}
  }, [soundEnabled, getAudioContext]);

  const playUnlock = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      // Mechanical clunk sequence
      [0, 0.1, 0.2, 0.35].forEach((delay, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = [200, 150, 100, 80][i];
        osc.type = 'square';
        gain.gain.setValueAtTime(0.15, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.1);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.1);
      });
      // Success chime
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 523.25; // C5
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.5);
      }, 400);
    } catch (e) {}
  }, [soundEnabled, getAudioContext]);

  const playError = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 200;
      osc.type = 'square';
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  }, [soundEnabled, getAudioContext]);

  return { playClick, playTurn, playUnlock, playError, soundEnabled, setSoundEnabled };
};

/* ─── Vault Wheel SVG ─────────────────────────────────────────── */
function VaultWheel({ rotation, isUnlocking, isUnlocked }) {
  const notchCount = 72;
  const majorEvery = 6;
  return (
    <motion.div
      className="relative"
      animate={
        isUnlocked
          ? { scale: [1, 1.08, 0.3], opacity: [1, 1, 0] }
          : isUnlocking
          ? { scale: [1, 1.02, 1] }
          : {}
      }
      transition={isUnlocked ? { duration: 0.8, ease: 'easeInOut' } : { duration: 0.3 }}
    >
      <svg viewBox="0 0 240 240" className="w-48 h-48 md:w-56 md:h-56" style={{ filter: 'drop-shadow(0 0 30px rgba(212,175,55,0.12))' }}>
        <defs>
          <radialGradient id="outerRing" cx="50%" cy="50%" r="50%">
            <stop offset="85%" stopColor="#181818" />
            <stop offset="100%" stopColor="#0a0a0a" />
          </radialGradient>
          <radialGradient id="innerDial" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#222" />
            <stop offset="100%" stopColor="#111" />
          </radialGradient>
          <radialGradient id="hubGrad" cx="45%" cy="40%" r="55%">
            <stop offset="0%" stopColor="#2a2a2a" />
            <stop offset="100%" stopColor="#0e0e0e" />
          </radialGradient>
          <filter id="innerShadow">
            <feFlood floodColor="#000" floodOpacity="0.4" />
            <feComposite in2="SourceGraphic" operator="in" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="SourceGraphic" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" />
          </filter>
        </defs>

        <circle cx="120" cy="120" r="116" fill="#0a0a0a" stroke="#222" strokeWidth="0.5" />
        <circle cx="120" cy="120" r="110" fill="url(#outerRing)" stroke="#1a1a1a" strokeWidth="1" />
        <circle cx="120" cy="120" r="105" fill="none" stroke="#D4AF37" strokeWidth="0.3" opacity="0.4" />

        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i / 12) * 360;
          const rad = (angle * Math.PI) / 180;
          const cx = 120 + 108 * Math.cos(rad);
          const cy = 120 + 108 * Math.sin(rad);
          return <circle key={`rivet-${i}`} cx={cx} cy={cy} r="2" fill="#1a1a1a" stroke="#2a2a2a" strokeWidth="0.5" />;
        })}

        <motion.g
          animate={{ rotate: rotation }}
          transition={{ type: 'spring', stiffness: 60, damping: 16 }}
          style={{ transformOrigin: '120px 120px' }}
        >
          <circle cx="120" cy="120" r="98" fill="url(#innerDial)" filter="url(#innerShadow)" />
          
          {Array.from({ length: notchCount }).map((_, i) => {
            const angle = (i / notchCount) * 360;
            const isMajor = i % majorEvery === 0;
            const isMid = i % (majorEvery / 2) === 0 && !isMajor;
            const r1 = isMajor ? 82 : isMid ? 86 : 89;
            const r2 = 95;
            const rad = (angle * Math.PI) / 180;
            return (
              <line
                key={i}
                x1={120 + r1 * Math.cos(rad)}
                y1={120 + r1 * Math.sin(rad)}
                x2={120 + r2 * Math.cos(rad)}
                y2={120 + r2 * Math.sin(rad)}
                stroke={isMajor ? '#D4AF37' : isMid ? '#444' : '#2a2a2a'}
                strokeWidth={isMajor ? 2.5 : isMid ? 1.2 : 0.6}
                strokeLinecap="round"
              />
            );
          })}

          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i / 12) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const cx = 120 + 74 * Math.cos(rad);
            const cy = 120 + 74 * Math.sin(rad);
            return (
              <text key={`num-${i}`} x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                fill="#D4AF37" fontSize="7" fontFamily="JetBrains Mono" opacity="0.6"
              >
                {i * 5}
              </text>
            );
          })}

          {[0, 120, 240].map((angle) => {
            const rad = (angle * Math.PI) / 180;
            return (
              <g key={`spoke-${angle}`}>
                <line
                  x1={120 + 22 * Math.cos(rad)} y1={120 + 22 * Math.sin(rad)}
                  x2={120 + 62 * Math.cos(rad)} y2={120 + 62 * Math.sin(rad)}
                  stroke="#1e1e1e" strokeWidth="8" strokeLinecap="round"
                />
                <line
                  x1={120 + 22 * Math.cos(rad)} y1={120 + 22 * Math.sin(rad)}
                  x2={120 + 62 * Math.cos(rad)} y2={120 + 62 * Math.sin(rad)}
                  stroke="#2a2a2a" strokeWidth="6" strokeLinecap="round"
                />
                <circle cx={120 + 42 * Math.cos(rad)} cy={120 + 42 * Math.sin(rad)} r="3.5" fill="#1a1a1a" stroke="#333" strokeWidth="0.8" />
              </g>
            );
          })}

          <circle cx="120" cy="120" r="20" fill="url(#hubGrad)" stroke="#333" strokeWidth="1" />
          <circle cx="120" cy="120" r="14" fill="#111" stroke="#2a2a2a" strokeWidth="0.5" />
          <circle cx="120" cy="120" r="7" fill="#D4AF37" opacity="0.25" />
          <circle cx="120" cy="120" r="3" fill="#D4AF37" opacity="0.5" />
          <rect x="114" y="30" width="12" height="8" rx="3" fill="#1a1a1a" stroke="#D4AF37" strokeWidth="0.8" opacity="0.7" />
        </motion.g>

        <polygon points="120,2 115,14 125,14" fill="#D4AF37" />
        <line x1="120" y1="14" x2="120" y2="22" stroke="#D4AF37" strokeWidth="1.5" opacity="0.5" />
      </svg>
    </motion.div>
  );
}

/* ─── Tumbler Dots ─────────────────────────────────────────────── */
function TumblerDots({ count, max = 20 }) {
  return (
    <div className="flex items-center justify-center gap-1.5 h-5">
      {Array.from({ length: Math.min(count, max) }).map((_, i) => (
        <motion.div
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-primary"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 12, delay: 0.02 }}
        />
      ))}
    </div>
  );
}

/* ─── Main Login Page ──────────────────────────────────────────── */
export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [isSignup, setIsSignup] = useState(searchParams.get('signup') === 'true');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [rotationHistory, setRotationHistory] = useState([]);
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const lastPasswordRef = useRef('');
  
  const { playClick, playTurn, playUnlock, playError, soundEnabled, setSoundEnabled } = useVaultSounds();

  useEffect(() => {
    if (searchParams.get('signup') === 'true') {
      setIsSignup(true);
    }
  }, [searchParams]);

  // Calculate rotation with alternating directions (clockwise/anticlockwise)
  const calculateRotation = useCallback(() => {
    let totalRotation = 0;
    const chars = password.split('');
    chars.forEach((char, index) => {
      // Alternate direction: even index = clockwise (+), odd index = anticlockwise (-)
      const direction = index % 2 === 0 ? 1 : -1;
      const charCode = char.charCodeAt(0);
      const rotationAmount = (charCode % 36) * 10; // 0-350 degrees based on character
      totalRotation += direction * rotationAmount;
    });
    return totalRotation;
  }, [password]);

  const wheelRotation = calculateRotation();

  // Play sound on password change
  useEffect(() => {
    const prev = lastPasswordRef.current;
    const curr = password;
    
    if (curr.length > prev.length) {
      // Character added
      const direction = (curr.length - 1) % 2 === 0 ? 1 : -1;
      playTurn(direction);
      playClick();
    } else if (curr.length < prev.length) {
      // Character removed
      playClick();
    }
    
    lastPasswordRef.current = curr;
  }, [password, playTurn, playClick]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setUnlocking(true);
    try {
      const res = isSignup
        ? await signup({ name, email, password })
        : await login({ email, password });
      playUnlock();
      setUnlocked(true);
      loginUser(res.data);
      setTimeout(() => navigate('/', { replace: true }), 900);
    } catch (err) {
      playError();
      setError(err.response?.data?.detail || 'Combination rejected');
      setUnlocking(false);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6" data-testid="login-page">
      <div className="grain-overlay" />

      {/* Sound Toggle */}
      <button
        onClick={() => setSoundEnabled(!soundEnabled)}
        className="fixed top-4 right-4 p-2 text-white/30 hover:text-white/60 transition-colors z-50"
        title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
      >
        {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </button>

      <AnimatePresence mode="wait">
        {!unlocked ? (
          <motion.div
            key="vault"
            className="w-full max-w-sm flex flex-col items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Vault Wheel */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <VaultWheel rotation={wheelRotation} isUnlocking={unlocking} isUnlocked={unlocked} />
            </motion.div>

            {/* Brand */}
            <motion.div
              className="text-center mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h1 className="font-display text-2xl font-bold uppercase tracking-[0.15em] mb-1">
                SŌHM
              </h1>
              <p className="text-xs text-muted-foreground tracking-widest uppercase">
                {isSignup ? 'Register Your Vault' : 'Enter Combination'}
              </p>
            </motion.div>

            {/* Rotation indicator */}
            <div className="mb-4 text-center">
              <TumblerDots count={password.length} />
              {password.length > 0 && (
                <p className="text-[10px] text-muted-foreground/50 mt-1">
                  {password.length % 2 === 0 ? '↻ Clockwise' : '↺ Counter-clockwise'}
                </p>
              )}
            </div>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  className="w-full bg-destructive/10 border border-destructive/20 text-destructive text-xs text-center p-2.5 rounded-sm mb-4"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  data-testid="auth-error"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <motion.form
              ref={formRef}
              onSubmit={handleSubmit}
              className="w-full space-y-3"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <AnimatePresence>
                {isSignup && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Input
                      data-testid="signup-name-input"
                      placeholder="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-transparent border-white/8 focus:border-primary/60 h-11 text-sm placeholder:text-white/20 rounded-sm"
                      required
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <Input
                data-testid="auth-email-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-white/8 focus:border-primary/60 h-11 text-sm placeholder:text-white/20 rounded-sm"
                required
              />

              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  type={showPw ? 'text' : 'password'}
                  placeholder="Vault combination"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-transparent border-white/8 focus:border-primary/60 h-11 text-sm placeholder:text-white/20 pr-10 font-mono tracking-widest rounded-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50 transition-colors"
                >
                  {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>

              <Button
                data-testid="auth-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-primary/90 text-primary-foreground font-bold uppercase tracking-[0.15em] text-xs hover:bg-primary rounded-sm h-11 transition-all duration-300"
              >
                {loading ? (
                  <motion.span
                    animate={{ opacity: [1, 0.4, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    Unlocking...
                  </motion.span>
                ) : isSignup ? 'Forge Vault' : 'Unlock'}
              </Button>
            </motion.form>

            {/* Divider */}
            <div className="flex items-center gap-4 my-5 w-full">
              <div className="flex-1 h-px bg-white/5" />
              <span className="text-[10px] text-white/20 uppercase tracking-[0.3em]">or</span>
              <div className="flex-1 h-px bg-white/5" />
            </div>

            {/* Google */}
            <Button
              data-testid="google-login-button"
              onClick={handleGoogle}
              variant="ghost"
              className="w-full border border-white/5 text-white/50 hover:text-white hover:bg-white/5 rounded-sm h-10 text-xs tracking-wide"
            >
              <svg className="w-3.5 h-3.5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            {/* Toggle */}
            <p className="text-center text-xs text-white/25 mt-5">
              {isSignup ? 'Already have a vault?' : 'No vault yet?'}{' '}
              <button
                data-testid="auth-toggle-mode"
                onClick={() => { setIsSignup(!isSignup); setError(''); }}
                className="text-primary/70 hover:text-primary transition-colors"
              >
                {isSignup ? 'Unlock' : 'Forge One'}
              </button>
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="opened"
            className="text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="w-20 h-20 rounded-full border-2 border-primary mx-auto mb-4 flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 10 }}
            >
              <motion.svg
                viewBox="0 0 24 24"
                className="w-8 h-8 text-primary"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
              >
                <motion.path
                  d="M5 12l5 5L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                />
              </motion.svg>
            </motion.div>
            <p className="font-display text-lg font-bold uppercase tracking-widest text-primary">
              Vault Open
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
